// @flow

export type DependentProperty = {
  properties: {
    alertType: {
      const: string,
    },
    [alertType: string]: DependentProperty,
  },
  required: Array<string>,
};

export type DependentAlertSchema = {
  dependencies: {
    [alertType: string]: {
      oneOf: Array<DependentProperty>,
    },
  },
};

export const findDependentAlertTypeSchema = (
  schema: ?DependentAlertSchema,
  propertyKey: string,
): ?Object => {
  if (!schema) {
    return null;
  }

  return schema.dependencies.alertType.oneOf.find(
    a => a.properties.alertType.const === propertyKey,
  );
};

export const findSchemaRequired = (
  schema: ?DependentProperty,
  alertType: string,
): Array<string> => {
  if (!schema) {
    return [];
  }

  return (schema.properties[alertType] || {}).required;
};

export const findSchemaPropertyByName = (
  schema: ?DependentProperty,
  alertType: string,
  propertyName: string,
): Object => {
  if (!schema) {
    return {};
  }

  return (schema.properties[alertType] || {}).properties[propertyName];
};

export const baseUiSchema = (t: (s: string) => string) => ({
  "ui:order": [
    "name",
    "isActive",
    "alertType",
    "absenceType",
    "alertPurpose",
    "triggerMode",
    "activeFrom",
    "activeTo",
    "*",
  ],
  name: {
    "ui:title": t("ALERTS_ALERT_NAME"),
  },
  isActive: {
    "ui:title": t("STATUS"),
    "ui:widget": "toggle",
    "ui:options": {
      labels: [
        { label: "Active", value: true },
        { label: "Inactive", value: false },
      ],
    },
  },
  alertType: {
    "ui:widget": "hidden",
  },
  absenceType: {
    "ui:title": t("ALERTS_ALERT_ABSENCE_TYPES_COVERED"),
  },
  triggerMode: {
    "ui:title": t("ALERTS_ALERT_TRIGGER_POINT"),
  },
  activeFrom: {
    "ui:title": t("ALERTS_ALERT_VALID_FROM"),
    "ui:widget": "date-picker",
  },
  activeTo: {
    "ui:title": t("ALERTS_ALERT_END_DATE"),
    "ui:widget": "date-picker",
  },
});

export default {
  type: "object",
  required: [
    "absenceType",
    "name",
    "isActive",
    "triggerMode",
    "activeFrom",
    "alertType",
    "alertPurpose",
  ],
  title: "Details",
  properties: {
    absenceType: {
      type: "string",
      title: "Absence Type",
      oneOf: [
        {
          title: "All",
          const: "ALL",
        },
        {
          title: "Medical only",
          const: "MEDICAL_ONLY",
        },
        {
          title: "Non medical only",
          const: "NON_MEDICAL_ONLY",
        },
      ],
    },
    isActive: {
      type: "boolean",
      title: "Is Active",
    },
    activeTo: {
      type: "string",
      title: "Active To",
      format: "date-time",
    },
    name: {
      type: "string",
      title: "Name",
    },
    triggerMode: {
      type: "string",
      title: "Trigger Mode",
      oneOf: [
        {
          title: "Actual time",
          const: "ACTUAL_TIME",
        },
        {
          title: "On close",
          const: "ON_CLOSE",
        },
        {
          title: "Predicted",
          const: "PREDICTED",
        },
      ],
    },
    activeFrom: {
      type: "string",
      title: "Active From",
      format: "date-time",
    },
    alertType: {
      type: "number",
      title: "Alert Type",
      oneOf: [
        {
          title: "Absence rate percentage",
          const: "ABSENCE_RATE_PERCENTAGE",
        },
        {
          title: "Absence reason alert type",
          const: "ABSENCE_REASON_ALERT_TYPE",
        },
        {
          title: "Bradford score over x in y months",
          const: "BRADFORD_SCORE_OVER_X_IN_Y_MONTHS",
        },
        {
          title: "Hours lost over avg wch in y period over z period type",
          const: "HOURS_LOST_OVER_AVG_WCH_IN_Y_PERIOD_OVER_Z_PERIOD_TYPE",
        },
        {
          title: "Work related",
          const: "WORK_RELATED",
        },
        {
          title: "X day type in y period type",
          const: "X_DAY_TYPE_IN_Y_PERIOD_TYPE",
        },
        {
          title: "X day type of continuous absence",
          const: "X_DAY_TYPE_OF_CONTINUOUS_ABSENCE",
        },
        {
          title: "X day type or z spells in y period type",
          const: "X_DAY_TYPE_OR_Z_SPELLS_IN_Y_PERIOD_TYPE",
        },
        {
          title: "X day type over z spells in y period type",
          const: "X_DAY_TYPE_OVER_Z_SPELLS_IN_Y_PERIOD_TYPE",
        },
        {
          title: "X hours in y period type",
          const: "X_HOURS_IN_Y_PERIOD_TYPE",
        },
        {
          title: "X spells in y period type",
          const: "X_SPELLS_IN_Y_PERIOD_TYPE",
        },
        {
          title: "X spells of same reason in y period type",
          const: "X_SPELLS_OF_SAME_REASON_IN_Y_PERIOD_TYPE",
        },
      ],
    },
    alertPurpose: {
      type: "string",
      title: "Alert Purpose",
      oneOf: [
        {
          title: "Reference alert",
          const: "REFERENCE_ALERT",
        },
        {
          title: "Monitoring prompt",
          const: "MONITORING_PROMPT",
        },
        {
          title: "Wellbeing referral",
          const: "WELLBEING_REFERRAL",
        },
      ],
    },
  },
};
